import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../api/axios";
import loadStatus from "../util/loadStatus";

export const getDonors = createAsyncThunk(
  "donors/getDonors",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().donors;
      if (state.count.getDonors <= 1) {
        const res = await axios.get("/donors", {
          params: { province_id: reqData },
        });
        return res.data;
      }
      return { count: state.total, data: state.data };
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getEmailedDonors = createAsyncThunk(
  "donors/getEmailedDonors",
  async ({ id, data }, { rejectWithValue, getState }) => {
    try {
      const state = getState().donors;
      if (state.count.getEmailedDonors <= 1) {
        const res = await axios.get("/donors/emailed/" + id, {
          params: data,
        });
        return res.data;
      }
      return { count: state.total, data: state.data };
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getDonor = createAsyncThunk(
  "donors/getDonor",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().donors;
      if (state.count.getDonor <= 1) {
        const res = await axios.get("/donor/" + reqData);
        return res.data;
      }
      return { ...state.selected, reformulations: state.reformulations };
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const addDonor = createAsyncThunk(
  "donors/addDonor",
  async (reqData, { rejectWithValue }) => {
    try {
      const res = await axios.post("/add-donor", reqData);

      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const editDonor = createAsyncThunk(
  "donors/editDonor",
  async ({ id, reqData }, { rejectWithValue }) => {
    try {
      const res = await axios.put("/edit-donor/" + id, reqData);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const editReformulations = createAsyncThunk(
  "donors/editReformulations",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await axios.put("/donors/edit-reformulations/" + id, data);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const deleteDonor = createAsyncThunk(
  "donors/deleteDonor",
  async ({ id, reqData }, { rejectWithValue }) => {
    try {
      const res = await axios.delete("/delete-donor/" + id, {
        params: reqData,
      });
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

const donorsSlice = createSlice({
  name: "donors",
  initialState: {
    total: 0,
    data: [],
    reformulations: [],
    selected: {},
    loading: false,
    error: null,
    load: {
      getDonors: false,
      getDonor: false,
      addDonor: false,
      editDonor: false,
      deleteDonor: false,
      editReformulations: false,
      getEmailedDonors: false,
    },
    count: { getDonors: 0, getDonor: 0, getEmailedDonors: 0 },
  },
  reducers: {
    setDonors: (state, action) => {
      state.data = action.payload;
    },
    setSelectedDonor: (state, action) => {
      state.selected = action.payload;
    },
    unsetDonors: (state) => {
      state.data = [];
      state.selected = {};
      state.reformulations = [];
    },
    unsetSelectedDonor: (state) => {
      state.selected = {};
      state.reformulations = [];
    },
  },
  extraReducers: (builder) => {
    builder
      //Get all Donors
      .addCase(getDonors.pending, (state) => {
        state.load.getDonors = loadStatus.loading;
        state.error = null;
        state.count.getDonors = ++state.count.getDonors;
      })
      .addCase(getDonors.fulfilled, (state, action) => {
        state.load.getDonors = loadStatus.success;
        state.data = action.payload.data;
        state.total = action.payload.count;
        state.count.getDonors = 0;
      })
      .addCase(getDonors.rejected, (state, action) => {
        state.load.getDonors = loadStatus.error;
        state.error = action.error;
        state.count.getDonors = 0;
      })
      //Get all Emailed Donors
      .addCase(getEmailedDonors.pending, (state) => {
        state.load.getEmailedDonors = loadStatus.loading;
        state.error = null;
        state.count.getEmailedDonors = ++state.count.getEmailedDonors;
      })
      .addCase(getEmailedDonors.fulfilled, (state, action) => {
        state.load.getEmailedDonors = loadStatus.success;
        state.data = action.payload.data;
        state.total = action.payload.count;
        state.count.getEmailedDonors = 0;
      })
      .addCase(getEmailedDonors.rejected, (state, action) => {
        state.load.getEmailedDonors = loadStatus.error;
        state.error = action.error;
        state.count.getEmailedDonors = 0;
      })
      //Get  Donor
      .addCase(getDonor.pending, (state) => {
        state.load.getDonor = loadStatus.loading;
        state.error = null;
        state.count.getDonor = ++state.count.getDonor;
      })
      .addCase(getDonor.fulfilled, (state, action) => {
        state.load.getDonor = loadStatus.success;
        state.selected = action.payload;
        state.reformulations = action.payload.reformulations;
        state.count.getDonor = 0;
      })
      .addCase(getDonor.rejected, (state, action) => {
        state.load.getDonor = loadStatus.error;
        state.error = action.error;
        state.count.getDonor = 0;
      })
      //Add Donation
      .addCase(addDonor.pending, (state) => {
        state.load.addDonor = loadStatus.loading;
        state.error = null;
      })
      .addCase(addDonor.fulfilled, (state, action) => {
        state.load.addDonor = loadStatus.success;
        state.data.unshift(action.payload);
      })
      .addCase(addDonor.rejected, (state, action) => {
        state.load.addDonor = loadStatus.error;
        state.error = action.error;
      })
      //Update Donation
      .addCase(editDonor.pending, (state) => {
        state.load.editDonor = loadStatus.loading;
        state.error = null;
      })
      .addCase(editDonor.fulfilled, (state, action) => {
        state.load.editDonor = loadStatus.success;
        const index = state.data.findIndex(
          (item, index) => item.id === action.payload.id
        );
        if (index !== -1) {
          state.data[index] = action.payload;
        }
        state.selected = action.payload;
      })
      .addCase(editDonor.rejected, (state, action) => {
        state.load.editDonor = loadStatus.error;
        state.error = action.error;
      })
      //Delete Donation
      .addCase(deleteDonor.pending, (state) => {
        state.load.deleteDonor = loadStatus.loading;
        state.error = null;
      })
      .addCase(deleteDonor.fulfilled, (state, action) => {
        state.load.deleteDonor = loadStatus.success;
        state.data.filter((item) => item.id !== action.meta.arg.id);
      })
      .addCase(deleteDonor.rejected, (state, action) => {
        state.load.deleteDonor = loadStatus.error;
        state.error = action.error;
      })
      //Edit Reformulations
      .addCase(editReformulations.pending, (state) => {
        state.load.editReformulations = loadStatus.loading;
        state.error = null;
      })
      .addCase(editReformulations.fulfilled, (state, action) => {
        state.load.editReformulations = loadStatus.success;
        state.reformulations = action.payload;
      })
      .addCase(editReformulations.rejected, (state, action) => {
        state.load.editReformulations = loadStatus.error;
        state.error = action.error;
      });
  },
});

export const { setDonors, setSelectedDonor, unsetDonors, unsetSelectedDonor } =
  donorsSlice.actions;
export default donorsSlice.reducer;
